/**
 * The two halves of a session ending: saying WHY on the way out, and getting
 * somebody back to WHERE they were on the way back in.
 *
 * ── Why ──
 *
 * A session here ends in four ways, and the sign-in page owes each a different
 * sentence (`components/SessionEndedCard.tsx` renders them):
 *
 *   idle       — 60 minutes without input (the in-app timer, or a 401 carrying
 *                SESSION_IDLE). Recorded by lib/sessionActivity.
 *   expired    — the 12-hour absolute cap (a 401 carrying SESSION_ABSOLUTE).
 *                Recorded by lib/sessionActivity.
 *   ended      — any other 401 while somebody WAS signed in: the server dropped
 *                the session for a reason it did not name. Deliberately silent
 *                about inactivity — blaming idleness would be a guess.
 *   elsewhere  — Sign Out was pressed in another tab or window of this browser.
 *                They share one cookie, so this tab's session is already gone.
 *
 * The note travels in sessionStorage because the login page usually renders
 * after a full navigation, which no in-memory state survives; it is per-tab so
 * one tab's sign-out explains nothing in another.
 *
 * NO APP IMPORTS, deliberately. `lib/queryClient` reads this module synchronously
 * on every 401, and `lib/sessionActivity` imports `apiRequest` from
 * queryClient — importing sessionActivity here would close that cycle. So the
 * idle/expired note is read and written through the SAME sessionStorage key
 * sessionActivity uses, and `tests/sessionEnd.test.ts` pins the two together
 * (a note recorded there must be read here). The one import, lib/publicPages,
 * is a leaf — it and shared/purchaseHost import nothing that could cycle.
 *
 * ── Where ──
 *
 * A deep link, a notification tap or a bookmark that lands on a lapsed session
 * is bounced to /login, and the destination was thrown away: signing in landed
 * on /home and the reader had to go and find the thing again. Now the path is
 * stashed on the way out and spent — once — on the way back in, including after
 * the round trip out to Google or Microsoft and back.
 */

import { isPublicPagePath } from "./publicPages";

export type SessionEndReason = "idle" | "expired" | "ended" | "elsewhere";

/* ─────────────────────────────── why ─────────────────────────────── */

/**
 * The note `lib/sessionActivity` writes for the two policy timeouts. Same key,
 * read here without importing it — see the note at the top of this file.
 */
const POLICY_REASON_KEY = "salefish:session:signedOutReason";

/** Per-tab note for the two reasons lib/sessionActivity does not carry. */
const END_REASON_KEY = "salefish:session:endReason";

/** Cross-tab: written to localStorage purely to fire `storage` in sibling tabs. */
export const SIGNED_OUT_ELSEWHERE_KEY = "salefish:session:signedOutElsewhere";

/** Leave a note for the login page. */
export function recordSessionEnd(reason: SessionEndReason): void {
  try {
    window.sessionStorage.setItem(
      reason === "idle" || reason === "expired" ? POLICY_REASON_KEY : END_REASON_KEY,
      reason,
    );
  } catch {
    /* private mode — the login page just renders without an explanation */
  }
}

function takeKey(key: string): string | null {
  try {
    const value = window.sessionStorage.getItem(key);
    if (value !== null) window.sessionStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

/**
 * Read and clear the note. A reload of the login page must not explain the
 * same sign-out twice.
 */
export function takeSessionEnd(): SessionEndReason | null {
  const policy = takeKey(POLICY_REASON_KEY);
  const other = takeKey(END_REASON_KEY);
  // A policy timeout is the more specific explanation when both are present:
  // an idle timeout broadcast to a sibling tab also produces a 401 there.
  if (policy === "idle" || policy === "expired") return policy;
  return other === "ended" || other === "elsewhere" ? other : null;
}

/** Whether a note is waiting, without spending it. */
export function peekSessionEnd(): boolean {
  try {
    return (
      window.sessionStorage.getItem(POLICY_REASON_KEY) !== null ||
      window.sessionStorage.getItem(END_REASON_KEY) !== null
    );
  } catch {
    return false;
  }
}

/** Tell the other tabs somebody pressed Sign Out here. */
export function broadcastSignedOutElsewhere(): void {
  try {
    window.localStorage.setItem(SIGNED_OUT_ELSEWHERE_KEY, String(Date.now()));
    window.localStorage.removeItem(SIGNED_OUT_ELSEWHERE_KEY);
  } catch {
    /* sibling tabs will find out on their next request instead */
  }
}

/* ─────────────────────── who we think is signed in ─────────────────────── */

/**
 * Whether this tab has seen a signed-in session since it loaded.
 *
 * A 401 means two different things depending on this. Before anyone signed in
 * it is an ordinary answer ("not signed in") that the auth state absorbs
 * quietly; after, it is a session that ENDED, and the reader is owed a trip to
 * the sign-in page with an explanation rather than a screen that silently goes
 * empty. `AuthContext` keeps it current.
 */
let knownSignedIn = false;

export function setKnownSignedIn(value: boolean): void {
  knownSignedIn = value;
}

export function isKnownSignedIn(): boolean {
  return knownSignedIn;
}

/* ─────────────────────────────── where ─────────────────────────────── */

const RETURN_KEY = "salefish:auth:returnTo";

/**
 * How long a stashed destination stays worth honouring. An interrupted errand
 * nobody resumed within half an hour is not one any more — landing somebody
 * where they were headed before lunch is a surprise, not a courtesy.
 */
export const RETURN_MAX_AGE_MS = 30 * 60 * 1000;

/** A URL nobody typed is still a URL. Cap it rather than storing whatever fits. */
const RETURN_MAX_LENGTH = 512;

/**
 * Screens that are never a destination: the sign-in screens themselves
 * (restoring one loops), the API, and every public or buyer page
 * (lib/publicPages.ts) — nobody was bounced OFF those, and a rep who opened
 * a purchaser's signing link must not land on it after signing in.
 */
const NOT_A_DESTINATION = ["/login", "/forgot-password", "/reset-password", "/verify", "/api"];

const pathnameOf = (p: string) => p.split(/[?#]/)[0];

/**
 * Is this somewhere we are willing to send somebody after they sign in?
 *
 * Pure, and exported for the test, because it is the security-relevant half of
 * the feature: everything here exists to make sure the value cannot name
 * somewhere off this origin.
 */
export function isRestorablePath(path: unknown): path is string {
  if (typeof path !== "string" || !path || path.length > RETURN_MAX_LENGTH) return false;
  // Must be a path on this origin, and must LOOK like one to the browser too.
  if (!path.startsWith("/")) return false;
  // `//evil.example` and `/\evil.example` both start with a slash and both
  // resolve to ANOTHER ORIGIN — protocol-relative, and the backslash form
  // because browsers normalise `\` to `/` in authority position. This is the
  // open redirect, and the one check here that is not a nicety.
  if (/^\/[/\\]/.test(path)) return false;
  // A control character cannot appear in a path this app generated.
  if (/[\u0000-\u001f\u007f]/.test(path)) return false;
  const pathname = pathnameOf(path);
  // The root only redirects to /home; there is nothing there to come back to.
  if (pathname === "/") return false;
  if (isPublicPagePath(pathname)) return false;
  return !NOT_A_DESTINATION.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Remember where somebody was when the session ran out. Defaults to the current
 * location, which is what every caller wants.
 *
 * Refusing sign-in paths is what makes this safe to call from several places at
 * once: a dead session produces a burst of 401s, and whichever lands after the
 * redirect has already happened is a no-op rather than a stash of /login.
 */
export function rememberReturnTo(path: string = currentPath()): void {
  if (!isRestorablePath(path)) return;
  try {
    window.sessionStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* private mode — they land on /home instead */
  }
}

/**
 * Spend it: the path to return to, or `null` when there is nothing to restore.
 *
 * `null` rather than a fallback so a caller can tell "nowhere to go" from "go
 * home" — the session check runs this on every successful check, including an
 * ordinary reload deep inside the app, and a fallback there would teleport
 * somebody to /home for no reason.
 *
 * Cleared whether or not it is honoured. A stash that survives being read is a
 * stash that fires on some later sign-in nobody connected it to.
 */
export function consumeReturnTo(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (!isRestorablePath(path)) return null;
    if (typeof at !== "number" || now - at > RETURN_MAX_AGE_MS || at > now + 60_000) return null;
    return path;
  } catch {
    return null;
  }
}

/** Peek without spending — for the sign-in page to say where it will go. */
export function hasReturnTo(): boolean {
  try {
    return window.sessionStorage.getItem(RETURN_KEY) !== null;
  } catch {
    return false;
  }
}
